import React, { useState, useEffect, useRef } from 'react'
import {
  BookOpenIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  PhotoIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  FolderIcon,
  FolderOpenIcon,
  Squares2X2Icon,
  HomeIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { materialsApi, materialCategoriesApi } from '../../services/adminApi'
import { CategoryManagement } from './CategoryManagement'
import { BatchUploadModal } from './BatchUploadModal'
import { OpenPDFButton } from './OpenPDFButton'

// 接口定义
interface Material {
  id: number
  material_name: string
  material_name_en?: string
  description?: string
  description_en?: string
  language: string
  level?: string
  category: string
  total_pages?: number
  file_url?: string
  file_size?: number
  cover_image_url?: string
  is_active: number
  is_public: number
  visibility?: 'all' | 'teacher' | 'admin'
  uploaded_by?: number
  uploader_name?: string
  uploader_email?: string
  created_at: string
  updated_at: string
}

interface MaterialStats {
  overview: {
    total: number
    active: number
    inactive: number
    public: number
  }
  by_language: Array<{ language: string; count: number }>
  by_level: Array<{ level: string; count: number }>
  by_category: Array<{ category: string; count: number }>
  recent_materials: Material[]
}

// 语言选项
const languageOptions = [
  { value: 'zh-CN', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' }
]

// 级别选项
const levelOptions = [
  { value: 'beginner', label: '初级' },
  { value: 'elementary', label: '基础' },
  { value: 'intermediate', label: '中级' },
  { value: 'upper-intermediate', label: '中高级' },
  { value: 'advanced', label: '高级' }
]

// 可见性选项
const visibilityOptions = [
  { value: 'all', label: '所有人可见（管理员、教师、学生）' },
  { value: 'teacher', label: '仅教师和管理员可见' },
  { value: 'admin', label: '仅管理员可见' }
]

// 分类选项接口
interface CategoryOption {
  id: number
  name: string
  name_en?: string
  parent_id: number | null
  material_count?: number
  children?: CategoryOption[]
}

// 面包屑项接口
interface BreadcrumbItem {
  id: number | null
  name: string
}

export const MaterialsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'materials' | 'categories'>('materials')
  const [loading, setLoading] = useState(false)
  const [materials, setMaterials] = useState<Material[]>([])
  const [stats, setStats] = useState<MaterialStats | null>(null)
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null)
  const [dynamicCategories, setDynamicCategories] = useState<CategoryOption[]>([])
  const [flatCategories, setFlatCategories] = useState<CategoryOption[]>([])

  // 批量操作状态
  const [showBatchUpload, setShowBatchUpload] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  // 文件夹式浏览状态
  const [currentCategory, setCurrentCategory] = useState<CategoryOption | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: '全部教材' }])
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set())

  // 模态框状态
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [materialUsage, setMaterialUsage] = useState<{ hasActiveBookings: boolean; activeBookingsCount: number } | null>(null)

  // 筛选和搜索状态
  const [searchInputValue, setSearchInputValue] = useState('') // 搜索框输入内容
  const [searchTerm, setSearchTerm] = useState('') // 实际执行搜索的内容
  const [languageFilter, setLanguageFilter] = useState('')
  const [levelFilter, setLevelFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('') // 默认显示所有状态，避免遗留数据被隐藏
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 20

  // 表单状态
  const [formData, setFormData] = useState({
    material_name: '',
    material_name_en: '',
    description: '',
    description_en: '',
    language: '',
    level: '',
    category: '',
    total_pages: '',
    file_url: '',
    cover_image_url: '',
    is_public: true,
    visibility: 'all' as 'all' | 'teacher' | 'admin'
  })

  // 文件上传
  const fileInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  // 暂存待上传的文件（不立即上传，点击保存时才上传）
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingCover, setPendingCover] = useState<File | null>(null)

  // 加载教材列表
  const loadMaterials = async () => {
    // 性能优化：如果没有选择分类且没有输入搜索关键词，则不显示任何内容，减少服务器压力
    if (!currentCategory && !searchTerm) {
      setMaterials([])
      setTotal(0)
      setTotalPages(0)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await materialsApi.getMaterials({
        page: currentPage,
        limit,
        search: searchTerm || undefined,
        language: languageFilter || undefined,
        level: levelFilter || undefined,
        // 如果有当前分类，使用 category_id 筛选；否则不传，获取全部
        category_id: currentCategory?.id,
        is_active: activeFilter ? activeFilter === '1' : undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      })

      if (response.success) {
        setMaterials(response.data)
        setTotal(response.pagination?.total || 0)
        setTotalPages(Math.ceil((response.pagination?.total || 0) / limit))
      }
    } catch (error) {
      console.error('加载教材列表失败:', error)
      toast.error('加载教材列表失败')
    } finally {
      setLoading(false)
    }
  }

  // 加载统计数据
  const loadStats = async () => {
    try {
      const response = await materialsApi.getMaterialStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('加载统计数据失败:', error)
    }
  }

  useEffect(() => {
    loadMaterials()
    loadStats()
    loadCategories()
  }, [currentPage, searchTerm, languageFilter, levelFilter, categoryFilter, activeFilter, sortBy, sortOrder, activeTab, currentCategory])

  // 构建面包屑路径
  const buildBreadcrumbs = (category: CategoryOption | null, allCategories: CategoryOption[]) => {
    const crumbs: BreadcrumbItem[] = [{ id: null, name: '全部教材' }]

    if (!category) {
      setBreadcrumbs(crumbs)
      return
    }

    const findPath = (cats: CategoryOption[], targetId: number, path: BreadcrumbItem[]): BreadcrumbItem[] | null => {
      for (const cat of cats) {
        const currentPath = [...path, { id: cat.id, name: cat.name }]
        if (cat.id === targetId) {
          return currentPath
        }
        if (cat.children && cat.children.length > 0) {
          const result = findPath(cat.children, targetId, currentPath)
          if (result) return result
        }
      }
      return null
    }

    const path = findPath(allCategories, category.id, [])
    if (path) {
      setBreadcrumbs([...crumbs, ...path])
    }
  }

  useEffect(() => {
    buildBreadcrumbs(currentCategory, dynamicCategories)
  }, [currentCategory, dynamicCategories])

  // 切换分类展开/收起
  const toggleCategoryExpand = (categoryId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  // 选择分类
  const selectCategory = (category: CategoryOption | null) => {
    setCurrentCategory(category)
    setCurrentPage(1) // 重置分页
    if (category) {
      setExpandedCategories(prev => new Set([...prev, category.id]))
    }
  }

  // 面包屑导航点击
  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (item.id === null) {
      setCurrentCategory(null)
    } else {
      const findCategory = (cats: CategoryOption[], id: number): CategoryOption | null => {
        for (const cat of cats) {
          if (cat.id === id) return cat
          if (cat.children) {
            const found = findCategory(cat.children, id)
            if (found) return found
          }
        }
        return null
      }
      const cat = findCategory(dynamicCategories, item.id)
      if (cat) setCurrentCategory(cat)
    }
  }

  // 获取当前分类的子分类
  const getChildCategories = (): CategoryOption[] => {
    if (!currentCategory) {
      return dynamicCategories
    }
    return currentCategory.children || []
  }

  // 渲染分类树节点
  const renderCategoryTree = (cats: CategoryOption[], level: number = 0) => {
    return cats.map(cat => {
      const hasChildren = cat.children && cat.children.length > 0
      const isExpanded = expandedCategories.has(cat.id)
      const isSelected = currentCategory?.id === cat.id

      return (
        <div key={cat.id}>
          <div
            className={`flex items-center px-3 py-2 cursor-pointer rounded-lg transition-colors ${isSelected
              ? 'bg-purple-100 text-purple-700 font-medium'
              : 'hover:bg-gray-100 text-gray-700'
              }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
            onClick={() => selectCategory(cat)}
          >
            {hasChildren && (
              <button
                onClick={(e) => toggleCategoryExpand(cat.id, e)}
                className="mr-1 p-0.5 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4" />
                )}
              </button>
            )}
            {!hasChildren && <span className="w-5 mr-1" />}
            <FolderIcon className={`w-4 h-4 mr-2 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
            <span className="flex-1 truncate text-sm">{cat.name}</span>
            {cat.material_count !== undefined && cat.material_count > 0 && (
              <span className="text-xs text-gray-400 ml-2">({cat.material_count})</span>
            )}
          </div>
          {hasChildren && isExpanded && (
            <div>{renderCategoryTree(cat.children!, level + 1)}</div>
          )}
        </div>
      )
    })
  }

  // 加载分类列表
  const loadCategories = async () => {
    try {
      const [treeResponse, flatResponse] = await Promise.all([
        materialCategoriesApi.getCategories(false),
        materialCategoriesApi.getCategories(true)
      ])
      if (treeResponse.success) {
        setDynamicCategories(treeResponse.data || [])
      }
      if (flatResponse.success) {
        setFlatCategories(flatResponse.data || [])
      }
    } catch (error) {
      console.error('加载分类列表失败:', error)
    }
  }

  // 重置表单
  const resetForm = () => {
    setFormData({
      material_name: '',
      material_name_en: '',
      description: '',
      description_en: '',
      language: '',
      level: '',
      category: '',
      total_pages: '',
      file_url: '',
      cover_image_url: '',
      is_public: true,
      visibility: 'all' as 'all' | 'teacher' | 'admin'
    })
    // 清除暂存的文件
    setPendingFile(null)
    setPendingCover(null)
  }

  // 打开编辑模态框
  const openEditModal = (material: Material) => {
    setSelectedMaterial(material)
    setFormData({
      material_name: material.material_name,
      material_name_en: material.material_name_en || '',
      description: material.description || '',
      description_en: material.description_en || '',
      language: material.language,
      level: material.level || '',
      category: material.category,
      total_pages: material.total_pages?.toString() || '',
      file_url: material.file_url || '',
      cover_image_url: material.cover_image_url || '',
      is_public: material.is_public === 1,
      visibility: (material.visibility || 'all') as 'all' | 'teacher' | 'admin'
    })
    setShowEditModal(true)
  }

  // 文件选择处理（只暂存，不立即上传）
  const handleFileSelect = (file: File, type: 'material' | 'cover') => {
    if (type === 'material') {
      setPendingFile(file)
      // 显示文件名作为临时提示
      setFormData(prev => ({ ...prev, file_url: `待上传: ${file.name}` }))
    } else {
      setPendingCover(file)
      setFormData(prev => ({ ...prev, cover_image_url: `待上传: ${file.name}` }))
    }
  }

  // 上传文件（保存时调用）
  const uploadFiles = async (): Promise<{ file_url?: string; cover_image_url?: string }> => {
    const result: { file_url?: string; cover_image_url?: string } = {}

    try {
      setUploading(true)

      // 上传教材文件
      if (pendingFile) {
        const response = await materialsApi.uploadFile(pendingFile, 'material')
        if (response.success && response.data?.url) {
          result.file_url = response.data.url
        } else {
          throw new Error('教材文件上传失败')
        }
      }

      // 上传封面图片
      if (pendingCover) {
        const response = await materialsApi.uploadFile(pendingCover, 'cover')
        if (response.success && response.data?.url) {
          result.cover_image_url = response.data.url
        } else {
          throw new Error('封面图片上传失败')
        }
      }

      return result
    } finally {
      setUploading(false)
    }
  }

  // 创建教材
  const handleCreate = async () => {
    if (!formData.material_name || !formData.language || !formData.category) {
      toast.error('请填写必填字段')
      return
    }

    try {
      setLoading(true)

      // 先上传文件（如果有）
      let uploadedUrls: { file_url?: string; cover_image_url?: string } = {}
      if (pendingFile || pendingCover) {
        uploadedUrls = await uploadFiles()
      }

      // 使用上传后的URL，或保持原有URL（如果没有新文件）
      const finalFileUrl = uploadedUrls.file_url || (formData.file_url.startsWith('待上传:') ? undefined : formData.file_url)
      const finalCoverUrl = uploadedUrls.cover_image_url || (formData.cover_image_url.startsWith('待上传:') ? undefined : formData.cover_image_url)

      const response = await materialsApi.createMaterial({
        material_name: formData.material_name,
        material_name_en: formData.material_name_en || undefined,
        description: formData.description || undefined,
        description_en: formData.description_en || undefined,
        language: formData.language,
        level: formData.level || undefined,
        category: formData.category,
        total_pages: formData.total_pages ? parseInt(formData.total_pages) : undefined,
        file_url: finalFileUrl || undefined,
        cover_image_url: finalCoverUrl || undefined,
        is_public: formData.visibility === 'all',
        visibility: formData.visibility
      })

      if (response.success) {
        toast.success('教材创建成功')
        setShowCreateModal(false)
        resetForm()
        loadMaterials()
        loadStats()
      }
    } catch (error: any) {
      toast.error(error.message || '创建教材失败')
    } finally {
      setLoading(false)
    }
  }

  // 更新教材
  const handleUpdate = async () => {
    if (!selectedMaterial) return
    if (!formData.material_name || !formData.language || !formData.category) {
      toast.error('请填写必填字段')
      return
    }

    try {
      setLoading(true)

      // 先上传文件（如果有）
      let uploadedUrls: { file_url?: string; cover_image_url?: string } = {}
      if (pendingFile || pendingCover) {
        uploadedUrls = await uploadFiles()
      }

      // 使用上传后的URL，或保持原有URL（如果没有新文件）
      const finalFileUrl = uploadedUrls.file_url || (formData.file_url.startsWith('待上传:') ? undefined : formData.file_url)
      const finalCoverUrl = uploadedUrls.cover_image_url || (formData.cover_image_url.startsWith('待上传:') ? undefined : formData.cover_image_url)

      const response = await materialsApi.updateMaterial(selectedMaterial.id.toString(), {
        material_name: formData.material_name,
        material_name_en: formData.material_name_en || undefined,
        description: formData.description || undefined,
        description_en: formData.description_en || undefined,
        language: formData.language,
        level: formData.level || undefined,
        category: formData.category,
        total_pages: formData.total_pages ? parseInt(formData.total_pages) : undefined,
        file_url: finalFileUrl || undefined,
        cover_image_url: finalCoverUrl || undefined,
        is_public: formData.visibility === 'all',
        visibility: formData.visibility
      })

      if (response.success) {
        toast.success('教材更新成功')
        setShowEditModal(false)
        setSelectedMaterial(null)
        resetForm()
        loadMaterials()
      }
    } catch (error: any) {
      toast.error(error.message || '更新教材失败')
    } finally {
      setLoading(false)
    }
  }

  // 打开删除确认弹窗（先检查使用情况）
  const openDeleteConfirm = async (material: Material) => {
    setSelectedMaterial(material)
    setMaterialUsage(null)

    try {
      const response = await materialsApi.checkMaterialUsage(material.id.toString())
      if (response.success) {
        setMaterialUsage(response.data)
      }
    } catch (error) {
      console.error('检查教材使用情况失败:', error)
    }

    setShowDeleteConfirm(true)
  }

  // 删除教材
  const handleDelete = async (forceDelete: boolean = false) => {
    if (!selectedMaterial) return

    try {
      setLoading(true)
      const response = await materialsApi.deleteMaterial(selectedMaterial.id.toString(), forceDelete)

      if (response.success) {
        toast.success('教材删除成功')
        setShowDeleteConfirm(false)
        setSelectedMaterial(null)
        setMaterialUsage(null)
        loadMaterials()
        loadStats()
      } else if (response.error?.code === 'MATERIAL_IN_USE') {
        // 教材正在被使用，显示警告
        setMaterialUsage({
          hasActiveBookings: true,
          activeBookingsCount: response.error.activeBookingsCount || 0
        })
      } else {
        toast.error(response.error?.message || '删除教材失败')
      }
    } catch (error: any) {
      toast.error(error.message || '删除教材失败')
    } finally {
      setLoading(false)
    }
  }

  // 批量删除教材
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return

    if (!window.confirm(`确定要批量删除选中的 ${selectedIds.length} 个教材吗？此操作不可撤销。`)) {
      return
    }

    try {
      setLoading(true)
      let successCount = 0
      let failCount = 0

      for (const id of selectedIds) {
        try {
          const response = await materialsApi.deleteMaterial(id.toString(), true)
          if (response.success) {
            successCount++
          } else {
            failCount++
          }
        } catch (err) {
          failCount++
        }
      }

      toast.success(`批量处理完成：成功 ${successCount} 个${failCount > 0 ? `，失败 ${failCount} 个` : ''}`)
      setSelectedIds([])
      loadMaterials()
      loadStats()
    } catch (error) {
      toast.error('批量操作失败')
    } finally {
      setLoading(false)
    }
  }

  // 格式化文件大小
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // 获取语言标签
  const getLanguageLabel = (value: string) => {
    return languageOptions.find(opt => opt.value === value)?.label || value
  }

  // 获取级别标签
  const getLevelLabel = (value?: string) => {
    if (!value) return '-'
    return levelOptions.find(opt => opt.value === value)?.label || value
  }

  // 获取分类标签
  const getCategoryLabel = (value: string) => {
    // 先尝试从动态分类中查找（通过ID或名称）
    const numValue = parseInt(value)
    if (!isNaN(numValue)) {
      const cat = flatCategories.find(c => c.id === numValue)
      if (cat) return cat.name
    }
    const cat = flatCategories.find(c => c.name === value || c.name_en === value)
    return cat?.name || value
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">教材管理</h1>
          <p className="mt-1 text-sm text-gray-500">管理平台教学材料资源</p>
        </div>
        {activeTab === 'materials' && (
          <div className="flex gap-3">
            {selectedIds.length > 0 && (
              <button
                onClick={handleBatchDelete}
                className="inline-flex items-center px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                <TrashIcon className="w-5 h-5 mr-2" />
                批量删除 ({selectedIds.length})
              </button>
            )}
            <button
              onClick={() => setShowBatchUpload(true)}
              className="inline-flex items-center px-4 py-2 bg-white text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <CloudArrowUpIcon className="w-5 h-5 mr-2" />
              批量上传
            </button>
            <button
              onClick={() => {
                resetForm()
                setShowCreateModal(true)
              }}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              添加教材
            </button>
          </div>
        )}
      </div>

      {/* 标签页切换 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('materials')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'materials'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <BookOpenIcon className="w-5 h-5 inline mr-2" />
            教材列表
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'categories'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FolderIcon className="w-5 h-5 inline mr-2" />
            分类管理
          </button>
        </nav>
      </div>

      {/* 分类管理标签页 */}
      {activeTab === 'categories' && (
        <CategoryManagement />
      )}

      {/* 教材列表标签页 */}
      {activeTab === 'materials' && (
        <>
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BookOpenIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">总教材数</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview.total}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircleIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">已启用</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview.active}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <XCircleIcon className="w-6 h-6 text-gray-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">已禁用</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview.inactive}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <GlobeAltIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">公开教材</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview.public}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 内容区 */}
          <div className="space-y-4">
            {/* 面包屑导航 */}
            <div className="flex items-center space-x-2 text-sm bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={item.id ?? 'root'}>
                  {index > 0 && <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
                  <button
                    onClick={() => handleBreadcrumbClick(item)}
                    className={`flex items-center transition-colors ${index === breadcrumbs.length - 1
                      ? 'text-purple-600 font-medium'
                      : index === 0
                        ? 'text-purple-500 font-semibold hover:text-purple-700 bg-purple-50 px-2 py-1 rounded-md'
                        : 'text-gray-500 hover:text-purple-600'
                      }`}
                  >
                    {index === 0 && <HomeIcon className="w-4 h-4 mr-1" />}
                    {item.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* 分类快捷入口 */}
            {getChildCategories().length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">{currentCategory ? '子分类' : '分类目录'}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {getChildCategories().map(cat => (
                    <div
                      key={cat.id}
                      onClick={() => selectCategory(cat)}
                      className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition-all"
                    >
                      <FolderIcon className="w-5 h-5 text-purple-500 mr-2 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {cat.name}
                        </p>
                        {cat.material_count !== undefined && (
                          <p className="text-xs text-gray-400">{cat.material_count} 个教材</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 筛选和搜索 */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="搜索教材名称（回车搜索）..."
                      value={searchInputValue}
                      onChange={(e) => setSearchInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setSearchTerm(searchInputValue)
                          setCurrentPage(1)
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
                <select
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">所有语言</option>
                  {languageOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">所有级别</option>
                  {levelOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">所有状态</option>
                  <option value="1">已启用</option>
                  <option value="0">已禁用</option>
                </select>
              </div>
            </div>

            {/* 教材列表 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : materials.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchTerm
                      ? `未找到与 "${searchTerm}" 相关的教材`
                      : currentCategory
                        ? '该分类下暂无教材'
                        : '请在上方或左侧选择分类目录，或输入名称回车搜索'}
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={materials.length > 0 && selectedIds.length === materials.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds(materials.map(m => m.id))
                            } else {
                              setSelectedIds([])
                            }
                          }}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">教材信息</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">语言/级别</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">分类</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {materials.map((material) => (
                      <tr key={material.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(material.id)}
                            onChange={() => {
                              setSelectedIds(prev =>
                                prev.includes(material.id)
                                  ? prev.filter(id => id !== material.id)
                                  : [...prev, material.id]
                              )
                            }}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {material.cover_image_url ? (
                                <img className="h-10 w-10 rounded object-cover" src={material.cover_image_url} alt="" />
                              ) : (
                                <div className="h-10 w-10 rounded bg-purple-100 flex items-center justify-center">
                                  <BookOpenIcon className="h-6 w-6 text-purple-600" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{material.material_name}</div>
                              {material.material_name_en && (
                                <div className="text-sm text-gray-500">{material.material_name_en}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{getLanguageLabel(material.language)}</div>
                          <div className="text-sm text-gray-500">{getLevelLabel(material.level)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getCategoryLabel(material.category)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${material.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {material.is_active ? '已启用' : '已禁用'}
                          </span>
                          {material.is_public === 1 && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              公开
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(material.created_at).toLocaleDateString('zh-CN')}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedMaterial(material)
                              setShowDetailModal(true)
                            }}
                            className="text-gray-600 hover:text-gray-900 mr-3"
                            title="查看详情"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => openEditModal(material)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="编辑"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(material)}
                            className="text-red-600 hover:text-red-900"
                            title="删除"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 分页 */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      上一页
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      下一页
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        共 <span className="font-medium">{total}</span> 条记录，
                        第 <span className="font-medium">{currentPage}</span> / <span className="font-medium">{totalPages}</span> 页
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 创建/编辑模态框 */}
          {(showCreateModal || showEditModal) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {showCreateModal ? '添加教材' : '编辑教材'}
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        教材名称 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.material_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, material_name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="请输入中文名称"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        英文名称
                      </label>
                      <input
                        type="text"
                        value={formData.material_name_en}
                        onChange={(e) => setFormData(prev => ({ ...prev, material_name_en: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="English name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        语言 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.language}
                        onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">请选择语言</option>
                        {languageOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">级别</label>
                      <select
                        value={formData.level}
                        onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">请选择级别</option>
                        {levelOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        分类 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">请选择分类</option>
                        {flatCategories.map(cat => (
                          <option key={cat.id} value={cat.id.toString()}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="请输入教材描述"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">可见性</label>
                      <select
                        value={formData.visibility}
                        onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value as 'all' | 'teacher' | 'admin' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      >
                        {visibilityOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 文件上传 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">教材文件</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={formData.file_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, file_url: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="文件URL"
                        />
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.ppt,.pptx"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleFileSelect(file, 'material')
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        >
                          <CloudArrowUpIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">封面图片</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={formData.cover_image_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, cover_image_url: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="封面URL"
                        />
                        <input
                          type="file"
                          ref={coverInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleFileSelect(file, 'cover')
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => coverInputRef.current?.click()}
                          disabled={uploading}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        >
                          <PhotoIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false)
                      setShowEditModal(false)
                      setSelectedMaterial(null)
                      resetForm()
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    取消
                  </button>
                  <button
                    onClick={showCreateModal ? handleCreate : handleUpdate}
                    disabled={loading || uploading}
                    className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {loading ? '保存中...' : '保存'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 详情模态框 */}
          {showDetailModal && selectedMaterial && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-lg w-full">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">教材详情</h3>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-start">
                    {selectedMaterial.cover_image_url ? (
                      <img className="h-20 w-20 rounded object-cover" src={selectedMaterial.cover_image_url} alt="" />
                    ) : (
                      <div className="h-20 w-20 rounded bg-purple-100 flex items-center justify-center">
                        <BookOpenIcon className="h-10 w-10 text-purple-600" />
                      </div>
                    )}
                    <div className="ml-4">
                      <h4 className="text-lg font-medium text-gray-900">{selectedMaterial.material_name}</h4>
                      {selectedMaterial.material_name_en && (
                        <p className="text-sm text-gray-500">{selectedMaterial.material_name_en}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">语言：</span>{getLanguageLabel(selectedMaterial.language)}</div>
                    <div><span className="text-gray-500">级别：</span>{getLevelLabel(selectedMaterial.level)}</div>
                    <div><span className="text-gray-500">分类：</span>{getCategoryLabel(selectedMaterial.category)}</div>
                    <div><span className="text-gray-500">状态：</span>{selectedMaterial.is_active ? '已启用' : '已禁用'}</div>
                    <div><span className="text-gray-500">公开：</span>{selectedMaterial.is_public ? '是' : '否'}</div>
                  </div>

                  {selectedMaterial.description && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">描述：</p>
                      <p className="text-sm text-gray-700">{selectedMaterial.description}</p>
                    </div>
                  )}

                  {selectedMaterial.file_url && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">文件：</p>
                      <OpenPDFButton
                        pdfUrl={selectedMaterial.file_url}
                        title={`查看文件 ${selectedMaterial.file_size ? `(${formatFileSize(selectedMaterial.file_size)})` : ''}`}
                        className="text-sm text-purple-600 hover:underline flex items-center"
                      >
                        <DocumentTextIcon className="w-4 h-4 mr-1" />
                        查看文件 {selectedMaterial.file_size && `(${formatFileSize(selectedMaterial.file_size)})`}
                      </OpenPDFButton>
                    </div>
                  )}

                  <div className="text-sm text-gray-500 pt-4 border-t">
                    <p>上传者：{selectedMaterial.uploader_name || '-'}</p>
                    <p>创建时间：{new Date(selectedMaterial.created_at).toLocaleString('zh-CN')}</p>
                    <p>更新时间：{new Date(selectedMaterial.updated_at).toLocaleString('zh-CN')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 删除确认 */}
          {showDeleteConfirm && selectedMaterial && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">确认删除</h3>
                {materialUsage && materialUsage.hasActiveBookings && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-yellow-800 font-medium">警告：该教材正在使用中</p>
                        <p className="text-yellow-700 text-sm mt-1">
                          有 <span className="font-bold">{materialUsage.activeBookingsCount}</span> 个待处理或已确认的预约正在使用此教材。
                          删除后，这些预约的教材将被清空。
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-gray-600 mb-6">
                  确定要删除教材 "<span className="font-medium">{selectedMaterial.material_name}</span>" 吗？
                  <span className="text-red-600 font-medium">此操作将永久删除该教材及其文件，无法恢复。</span>
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false)
                      setSelectedMaterial(null)
                      setMaterialUsage(null)
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => handleDelete(materialUsage?.hasActiveBookings || false)}
                    disabled={loading}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? '删除中...' : (materialUsage?.hasActiveBookings ? '仍然删除' : '确认删除')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 批量上传模态框 */}
          {showBatchUpload && (
            <BatchUploadModal
              onClose={() => setShowBatchUpload(false)}
              onSuccess={() => {
                loadMaterials()
                loadStats()
                loadCategories() // 批量上传后刷新分类树，以显示新创建的级联目录
              }}
              categories={flatCategories}
              languageOptions={languageOptions}
              levelOptions={levelOptions}
              currentCategoryId={currentCategory?.id}
            />
          )}
        </>
      )}
    </div>
  )
}

export default MaterialsManagement
